import type { Sex } from '../api/types';
import { aquecerCabelo, cabeloHueNoIntervalo, corDoCabelo, ESTILO_OK } from './cabelo';
import { peleRgb, tingirFonte } from './pele';

export type Retangulo = readonly [number, number, number, number];
export type Ponto = readonly [number, number];
type PorCorpo<T> = Readonly<Record<Sex, T>>;

export interface EstiloDaTela {
  readonly id: string;
  /** null = sem arte (carta desligada). `none` nunca tem arte e nunca é desligado. */
  readonly arte: ArteDoEstilo | null;
}

interface ArteDoEstilo {
  readonly imagem: PorCorpo<string>;
  readonly imagemTras: PorCorpo<string> | null;
  readonly recorte: Retangulo | null;
  readonly origem: Ponto;
  readonly escala: number;
  readonly destino: PorCorpo<Ponto>;
}

export interface Personalizacao {
  readonly largura: number;
  readonly altura: number;
  readonly corpo: PorCorpo<string>;
  readonly roupaLimiar: number;
  readonly quadro: PorCorpo<Retangulo>;
  readonly cartaCabelo: PorCorpo<Retangulo>;
  readonly cartaBarba: PorCorpo<Retangulo>;
  readonly cabelo: readonly EstiloDaTela[];
  readonly barba: readonly EstiloDaTela[];
}

/** O que a tela pede para desenhar. */
export interface Visual {
  readonly sex: Sex;
  readonly skinHue: number;
  readonly hairStyle: string;
  readonly hairHue: number;
  readonly beardStyle: string;
  readonly beardHue: number;
}

export interface Montagem {
  readonly largura: number;
  readonly altura: number;
  readonly corpo: HTMLCanvasElement;
  readonly tras: readonly Posta[];
  readonly frente: readonly Posta[];
}

interface Posta {
  readonly fonte: CanvasImageSource;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

const CAMINHO_OK = /^[a-z0-9][a-z0-9_/-]*\.png$/;
const CORPO_MAX = 4;
const CAMADA_MAX = 24;

const imagens = new Map<string, Promise<HTMLImageElement>>();
const caixas = new Map<string, Retangulo | null>();
const corpos = new Map<string, HTMLCanvasElement>();
const camadas = new Map<string, HTMLCanvasElement>();

let carga: Promise<Personalizacao> | null = null;

export function carregarPersonalizacao(): Promise<Personalizacao> {
  carga ??= fetch('/personalizacao/personalizacao.json')
    .then(r => {
      if (!r.ok) throw new Error(`personalizacao.json ${r.status}`);
      return r.json();
    })
    .then(doc => interpretar(doc));
  return carga;
}

function interpretar(doc: unknown): Personalizacao {
  if (!doc || typeof doc !== 'object') throw new Error('manifesto vazio');
  const raiz = doc as Record<string, unknown>;
  if (raiz.versao !== 1) throw new Error('versão do manifesto');
  const avatar = objeto(raiz.avatar);
  const largura = numero(avatar?.largura);
  const altura = numero(avatar?.altura);
  const roupaLimiar = numero(avatar?.roupaLimiar);
  const corpo = avatar ? porCorpo(avatar.corpo, caminho) : null;
  if (largura == null || largura <= 0 || altura == null || altura <= 0 || roupaLimiar == null || !corpo) {
    throw new Error('corpo faltando');
  }
  const enq = objeto(raiz.enquadramento);
  const quadro = enq ? porCorpo(enq.quadro, retangulo) : null;
  const cartaCabelo = enq ? porCorpo(enq.cartaCabelo, retangulo) : null;
  const cartaBarba = enq ? porCorpo(enq.cartaBarba, retangulo) : null;
  if (!quadro || !cartaCabelo || !cartaBarba) throw new Error('enquadramento');
  return {
    largura, altura, corpo, roupaLimiar,
    quadro, cartaCabelo, cartaBarba,
    cabelo: estilos(raiz.cabelo),
    barba: estilos(raiz.barba)
  };
}

function estilos(raw: unknown): EstiloDaTela[] {
  if (!Array.isArray(raw)) return [];
  const vistos = new Set<string>();
  const saida: EstiloDaTela[] = [];
  for (const item of raw) {
    const estilo = estiloDe(item);
    if (!estilo || vistos.has(estilo.id)) {
      if (estilo) console.warn('[personalizacao] id repetido', estilo.id);
      continue;
    }
    vistos.add(estilo.id);
    saida.push(estilo);
  }
  return saida;
}

function estiloDe(raw: unknown): EstiloDaTela | null {
  const o = objeto(raw);
  if (!o || typeof o.id !== 'string' || !ESTILO_OK.test(o.id)) {
    console.warn('[personalizacao] id recusado', o?.id);
    return null;
  }
  if (o.id === 'none') return { id: 'none', arte: null };
  try {
    return { id: o.id, arte: arteDe(o) };
  } catch {
    console.warn('[personalizacao] item descartado', o.id);
    return null;
  }
}

function arteDe(o: Record<string, unknown>): ArteDoEstilo | null {
  if (o.imagem == null) return null;
  const imagem = porCorpo(o.imagem, caminho);
  if (!imagem) {
    console.warn('[personalizacao] sem arte', o.id);
    return null;
  }
  const imagemTras = o.imagemTras == null ? null : porCorpo(o.imagemTras, caminho);
  if (o.imagemTras != null && !imagemTras) {
    console.warn('[personalizacao] sem arte', o.id);
    return null;
  }
  const recorte = o.recorte == null ? null : retangulo(o.recorte);
  if (o.recorte != null && !recorte) throw new Error('recorte');
  const origem = o.origem == null ? [0, 0] as Ponto : ponto(o.origem);
  if (!origem) throw new Error('origem');
  const escala = o.escala == null ? 1 : numero(o.escala);
  if (escala == null || escala <= 0) throw new Error('escala');
  const destino = o.destino == null
    ? { male: [0, 0] as Ponto, female: [0, 0] as Ponto }
    : porCorpo(o.destino, ponto);
  if (!destino) throw new Error('destino');
  return { imagem, imagemTras, recorte, origem, escala, destino };
}

function caminho(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.includes('..') || !CAMINHO_OK.test(raw)) {
    console.warn('[personalizacao] caminho recusado', raw);
    return null;
  }
  return `/personalizacao/${raw}`;
}

function porCorpo<T>(raw: unknown, ler: (v: unknown) => T | null): PorCorpo<T> | null {
  const o = objeto(raw);
  if (o && ('male' in o || 'female' in o)) {
    const male = ler(o.male);
    const female = ler(o.female);
    if (male == null || female == null) return null;
    return { male, female };
  }
  const unico = ler(raw);
  if (unico == null) return null;
  return { male: unico, female: unico };
}

function objeto(raw: unknown): Record<string, unknown> | null {
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : null;
}

function numero(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

function ponto(raw: unknown): Ponto | null {
  if (!Array.isArray(raw) || raw.length < 2) return null;
  const x = numero(raw[0]);
  const y = numero(raw[1]);
  if (x == null || y == null) return null;
  return [x, y];
}

function retangulo(raw: unknown): Retangulo | null {
  if (!Array.isArray(raw) || raw.length < 4) return null;
  const x = numero(raw[0]);
  const y = numero(raw[1]);
  const w = numero(raw[2]);
  const h = numero(raw[3]);
  if (x == null || y == null || w == null || h == null || w <= 0 || h <= 0) return null;
  return [x, y, w, h];
}

function imagem(url: string): Promise<HTMLImageElement> {
  const pronta = imagens.get(url);
  if (pronta) return pronta;
  const nova = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(url));
    img.src = url;
  });
  imagens.set(url, nova);
  return nova;
}

function caixaDoAlfa(img: HTMLImageElement, recorte: Retangulo | null): Retangulo | null {
  const chave = `${img.src}#${recorte ? recorte.join(',') : 'toda'}`;
  if (caixas.has(chave)) return caixas.get(chave) ?? null;
  const x0 = recorte ? recorte[0] : 0;
  const y0 = recorte ? recorte[1] : 0;
  const w = Math.max(0, Math.min(recorte ? recorte[2] : img.naturalWidth, img.naturalWidth - x0));
  const h = Math.max(0, Math.min(recorte ? recorte[3] : img.naturalHeight, img.naturalHeight - y0));
  if (w <= 0 || h <= 0) {
    caixas.set(chave, null);
    return null;
  }
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  if (!g) return null;
  g.drawImage(img, x0, y0, w, h, 0, 0, w, h);
  const d = g.getImageData(0, 0, w, h).data;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (d[(y * w + x) * 4 + 3] === 0) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  const caixa: Retangulo | null = maxX < 0 ? null : [x0 + minX, y0 + minY, maxX - minX + 1, maxY - minY + 1];
  caixas.set(chave, caixa);
  return caixa;
}

function lembrar<T>(mapa: Map<string, T>, chave: string, valor: T, teto: number) {
  mapa.delete(chave);
  mapa.set(chave, valor);
  while (mapa.size > teto) {
    const velha = mapa.keys().next();
    if (velha.done) break;
    mapa.delete(velha.value);
  }
}

async function corpoTingido(p: Personalizacao, sex: Sex, skinHue: number): Promise<HTMLCanvasElement> {
  const chave = `${sex}#${skinHue}`;
  const hit = corpos.get(chave);
  if (hit) {
    lembrar(corpos, chave, hit, CORPO_MAX);
    return hit;
  }
  const img = await imagem(p.corpo[sex]);
  const fonte = tingirFonte(
    img, p.largura, p.altura, peleRgb(skinHue), 160,
    (r, g, b) => (r + g) / 2 - b >= p.roupaLimiar
  );
  if (!(fonte instanceof HTMLCanvasElement)) throw new Error('corpo');
  lembrar(corpos, chave, fonte, CORPO_MAX);
  return fonte;
}

async function camadaTingida(url: string, recorte: Retangulo | null, hue: number): Promise<HTMLCanvasElement | null> {
  let img: HTMLImageElement;
  try {
    img = await imagem(url);
  } catch (err) {
    console.warn('[personalizacao] imagem não carregou', url, err);
    return null;
  }
  const caixa = caixaDoAlfa(img, recorte);
  if (!caixa) return null;
  const tom = cabeloHueNoIntervalo(hue);
  const chave = `${url}#${caixa.join(',')}#${tom}`;
  const hit = camadas.get(chave);
  if (hit) {
    lembrar(camadas, chave, hit, CAMADA_MAX);
    return hit;
  }
  const [x, y, w, h] = caixa;
  const pedaco = document.createElement('canvas');
  pedaco.width = w;
  pedaco.height = h;
  const g = pedaco.getContext('2d');
  if (!g) return null;
  g.drawImage(img, x, y, w, h, 0, 0, w, h);
  const fonte = tingirFonte(pedaco, w, h, corDoCabelo(tom), 246);
  if (!(fonte instanceof HTMLCanvasElement)) return null;
  lembrar(camadas, chave, fonte, CAMADA_MAX);
  return fonte;
}

async function posta(
  url: string, arte: ArteDoEstilo, sex: Sex, hue: number
): Promise<Posta | null> {
  const canvas = await camadaTingida(url, arte.recorte, hue);
  if (!canvas) return null;
  const caixa = caixaDoAlfa(await imagem(url), arte.recorte);
  if (!caixa) return null;
  const [tx, ty, tw, th] = caixa;
  const e = arte.escala;
  const [ox, oy] = arte.origem;
  const [dx, dy] = arte.destino[sex];
  return {
    fonte: canvas,
    x: dx + (tx - ox) * e,
    y: dy + (ty - oy) * e,
    w: tw * e,
    h: th * e
  };
}

export async function montar(p: Personalizacao, v: Visual): Promise<Montagem> {
  await aquecerCabelo();
  const corpo = await corpoTingido(p, v.sex, v.skinHue);
  const tras: Posta[] = [];
  const frente: Posta[] = [];
  const cabelo = p.cabelo.find(e => e.id === v.hairStyle);
  if (cabelo?.arte?.imagemTras) {
    const atras = await posta(cabelo.arte.imagemTras[v.sex], cabelo.arte, v.sex, v.hairHue);
    if (atras) tras.push(atras);
  }
  if (v.sex === 'male') {
    const barba = p.barba.find(e => e.id === v.beardStyle);
    if (barba?.arte) {
      const pelo = await posta(barba.arte.imagem[v.sex], barba.arte, v.sex, v.beardHue);
      if (pelo) frente.push(pelo);
    }
  }
  if (cabelo?.arte) {
    const pelo = await posta(cabelo.arte.imagem[v.sex], cabelo.arte, v.sex, v.hairHue);
    if (pelo) frente.push(pelo);
  }
  return { largura: p.largura, altura: p.altura, corpo, tras, frente };
}

export function desenhar(canvas: HTMLCanvasElement, m: Montagem, recorte: Retangulo): void {
  const [x, y, w, h] = recorte;
  const sx = canvas.width / w;
  const sy = canvas.height / h;
  const g = canvas.getContext('2d');
  if (!g) return;
  g.clearRect(0, 0, canvas.width, canvas.height);
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  for (const c of m.tras) g.drawImage(c.fonte, (c.x - x) * sx, (c.y - y) * sy, c.w * sx, c.h * sy);
  g.drawImage(m.corpo, -x * sx, -y * sy, m.largura * sx, m.altura * sy);
  for (const c of m.frente) g.drawImage(c.fonte, (c.x - x) * sx, (c.y - y) * sy, c.w * sx, c.h * sy);
}
